import { format, isValid, parseISO } from 'date-fns';

import { Input } from '@/components/core/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';
import { TagsInput } from '@/components/tags-input';
import { assertNever, cn } from '@/shared/helpers';

import { EntitySelectInput } from './entity-select-input';
import { MultiSelectCombobox } from './multi-select-combobox';
import type { FieldInputProps } from './types';
import { asString, asStringArray } from './value-helpers';

/**
 * Renders the control for a field descriptor. Purely controlled — it owns no
 * form state and reads/writes through `value`/`onChange`, mirroring the core
 * inputs (`core/input`, `core/select`). `none` renders nothing (operand-less
 * operators like `IS_EMPTY`).
 */
export function FieldInput({
  descriptor,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  id,
  className,
  ...aria
}: FieldInputProps) {
  switch (descriptor.kind) {
    case 'text':
      return (
        <Input
          {...aria}
          id={id}
          className={className}
          value={asString(value)}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      );

    case 'number':
      return (
        <Input
          {...aria}
          id={id}
          type="number"
          className={className}
          inputMode={descriptor.integer ? 'numeric' : 'decimal'}
          step={descriptor.integer ? 1 : 'any'}
          value={value === undefined ? '' : String(value)}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === '') {
              onChange(undefined);
              return;
            }
            // `valueAsNumber`, not `parseInt`/`parseFloat`, so values like "1.5"
            // or "1e3" aren't silently truncated. Reject anything non-finite —
            // and non-integers for integer descriptors — rather than storing a
            // value the user never typed.
            const parsed = event.target.valueAsNumber;
            if (!Number.isFinite(parsed) || (descriptor.integer && !Number.isInteger(parsed))) {
              return;
            }
            onChange(parsed);
          }}
          onBlur={onBlur}
        />
      );

    case 'select':
      return (
        <Select value={asString(value) || undefined} disabled={disabled} onValueChange={onChange}>
          <SelectTrigger {...aria} id={id} className={cn('w-full', className)}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {descriptor.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'multi-select':
      return (
        <MultiSelectCombobox
          {...aria}
          id={id}
          options={descriptor.options}
          value={asStringArray(value)}
          disabled={disabled}
          placeholder={placeholder}
          emptyText="No matches"
          className={className}
          onChange={onChange}
          onBlur={onBlur}
        />
      );

    case 'tags':
      return (
        <TagsInput
          {...aria}
          id={id}
          className={className}
          value={asStringArray(value)}
          disabled={disabled}
          placeholder={placeholder}
          onValueChange={onChange}
          onBlur={onBlur}
        />
      );

    case 'entity-select':
      return (
        <EntitySelectInput
          {...aria}
          id={id}
          descriptor={descriptor}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          className={className}
          onChange={onChange}
          onBlur={onBlur}
        />
      );

    case 'datetime': {
      // Condition `date` properties are timestamps, so edit the time of day too
      // (the date-only core `DatePicker` would drop it). A native datetime-local
      // input works in local time, so format/parse locally and store as ISO.
      const selected = typeof value === 'string' && value ? parseISO(value) : undefined;
      return (
        <Input
          {...aria}
          id={id}
          type="datetime-local"
          className={className}
          value={selected && isValid(selected) ? format(selected, "yyyy-MM-dd'T'HH:mm") : ''}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => {
            const raw = event.target.value;
            onChange(raw ? new Date(raw).toISOString() : undefined);
          }}
          onBlur={onBlur}
        />
      );
    }

    case 'none':
      return null;

    default:
      return assertNever(descriptor);
  }
}
